/**
 * beta-interest-api.ts — client for the ADR-098 Beta Interest work queue.
 *
 * Thin typed wrappers over the admin-api routes:
 *   GET    /admin/beta-interest
 *   POST   /admin/beta-interest/{id}/transition
 *   GET    /admin/beta-interest/{id}/journey
 */
import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one per-record call to the record's own zone (ADR-122 Pattern 1).
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ANY". A lead exists in exactly one zone; transitioning
 * or reading the journey of a us-east-1 lead against ap-southeast-2 returns a 404 on a record
 * the operator is LOOKING AT, which reads as data loss rather than as a misrouted request. The
 * list tags every lead with `zone` precisely so the row's actions can pass it back.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

export type BetaInterestStatus = 'new' | 'contacted' | 'provisioned' | 'declined';

export interface BetaLead {
  id: string;
  capturedAt: string;
  businessName: string;
  email: string;
  emailDomain: string;
  domain: string;
  ownerName: string;
  promoCode: string;
  status: BetaInterestStatus;
  statusAt: string;
  statusBy: string;
  linkedAccountId: string;
  linkedCustomerNumber: string;
  sessionToken: string;
  hasJourney: boolean;
  /** ADR-124 D4 — the country the business declared; '' for a lead that predates the
   *  residency step. Optional on the wire so an older API response still parses. */
  jurisdiction?: string;

  /**
   * ADR-122 Pattern 1 — which zone this lead actually lives in.
   *
   * Added by the client after the fan-out, NOT returned by the server: each zone's API answers
   * only for itself and has no reason to name itself. Always populated for a lead that came
   * through `listAllBetaInterest`.
   */
  zone?: string;
}

export interface BetaInterestStats {
  new: number;
  contacted: number;
  provisioned: number;
  declined: number;
  total: number;
  conversion: number;
  oldestWaitingDays: number;
}

export interface BetaInterestList {
  leads: BetaLead[];
  stats: BetaInterestStats;
  notifyAddress: string;
}

/** One step of the captured journey (subset of the telemetry SessionStep). */
export interface JourneyStep {
  timestamp: string;
  eventType: string;
  pagePath: string;
  pageUrl: string;
  isEntry: boolean;
  dwellSeconds: number;
  journeyStep: string;
}

export interface JourneySession {
  sessionToken: string;
  hostname: string;
  startedAt: string;
  source: string;
  channel: string;
  pageCount: number;
  interactionCount: number;
  durationSeconds: number;
  steps: JourneyStep[];
}

export interface BetaJourneyResult {
  captured: boolean;
  sessionToken: string;
  session: JourneySession | null;
  configured?: boolean;
  error?: string;
}

function listQuery(params: { status?: string; q?: string }): string {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  return qs.toString() ? `?${qs.toString()}` : '';
}

/**
 * The result of a cross-zone beta-interest read.
 *
 * ⭐ `failedZones` IS PART OF THE ANSWER, NOT TELEMETRY. A zone that did not respond
 * contributes no leads, and a shorter queue is indistinguishable from a correct one — staff
 * would simply not know a jurisdiction's leads existed. The caller must render this.
 */
export interface BetaInterestResult extends BetaInterestList {
  /** Labels of zones that did not answer. Empty on a complete read. */
  failedZones: string[];
}

/**
 * Every beta-interest lead the operator may see, across every zone (ADR-122 Pattern 1).
 *
 * ⭐⭐ THIS PAGE IS THE ONE WITH A LIVE CONSEQUENCE TODAY, which is why it leads Pattern 1's
 * list in ADR-122. The public sign-up gate is CLOSED, so every sign-up attempt anywhere on the
 * platform becomes a lead in this queue — and before this change a lead captured in
 * `us-east-1` was invisible to staff entirely. Not mislabelled: absent, with nothing on screen
 * to suggest anything was missing.
 *
 * The fan-out runs in THIS BROWSER, one request per zone, each carrying the same globally-valid
 * staff token — see `lib/zones.ts` for why that is a compliance requirement and not a style
 * choice. With one zone configured this is exactly the single-zone read it replaced.
 */
export async function listAllBetaInterest(
  params: { status?: string; q?: string } = {},
): Promise<BetaInterestResult> {
  const suffix = listQuery(params);
  const results = await fanOutZones(async (zone: PortalZone) => {
    const page = await adminApiInZone<BetaInterestList>(zone, `/admin/beta-interest${suffix}`);
    // The whole payload is carried on each row so the merge below can reach the per-zone
    // stats without a second, differently-shaped fan-out.
    return [{ zone, page }];
  });

  // No zone answered → a failed read, not a partial one; the page's catch shows its error
  // toast rather than an empty queue that reads as "nobody has signed up".
  throwIfAllZonesFailed(results);

  const pages = results.flatMap((r) => r.items);

  return {
    // Newest first, so a merge of two zones reads as one queue rather than as AU-then-US.
    leads: pages
      .flatMap(({ zone, page }) => page.leads.map((l) => ({ ...l, zone: zone.code })))
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt)),
    stats: mergeStats(pages.map((p) => p.page.stats)),
    // The notification address is platform configuration rather than per-zone data; the home
    // zone's answer is the one to show, and it is identical across zones in practice.
    notifyAddress: pages.find(({ zone }) => zone.home)?.page.notifyAddress
      ?? pages[0]?.page.notifyAddress
      ?? '',
    failedZones: failedZoneLabels(results),
  };
}

/**
 * Combine each zone's stats block into one.
 *
 * ⚠ `conversion` IS RECOMPUTED, NEVER AVERAGED. It is `provisioned / total` as a percentage
 * (`lambdas/account/src/beta-interest.ts`), so averaging two zones' percentages would weight a
 * zone with three leads equally against one with three hundred. Recomputing from the summed
 * counts is the only merge that yields the figure the page claims to be showing.
 *
 * `oldestWaitingDays` takes the MAX rather than a sum or a mean: it answers "how long has the
 * longest-waiting lead been waiting anywhere", and the worst case is the honest answer.
 */
function mergeStats(all: BetaInterestStats[]): BetaInterestStats {
  const sum = (pick: (s: BetaInterestStats) => number) => all.reduce((n, s) => n + pick(s), 0);
  const total = sum((s) => s.total);
  const provisioned = sum((s) => s.provisioned);
  return {
    new: sum((s) => s.new),
    contacted: sum((s) => s.contacted),
    provisioned,
    declined: sum((s) => s.declined),
    total,
    conversion: total > 0 ? Math.round((provisioned / total) * 100) : 0,
    oldestWaitingDays: all.reduce((n, s) => Math.max(n, s.oldestWaitingDays), 0),
  };
}

export function transitionBetaInterest(
  id: string,
  toStatus: BetaInterestStatus,
  extra: { linkedAccountId?: string; linkedCustomerNumber?: string } = {},
  /** The lead's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BetaLead> {
  return call<BetaLead>(zone, `/admin/beta-interest/${encodeURIComponent(id)}/transition`, {
    method: 'POST',
    body: JSON.stringify({ toStatus, ...extra }),
  });
}

export function getBetaInterestJourney(
  id: string,
  /** The lead's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<BetaJourneyResult> {
  return call<BetaJourneyResult>(zone, `/admin/beta-interest/${encodeURIComponent(id)}/journey`);
}
